import { apiFetch } from "../api/client";
import { countryCode } from "./billCountries";
import { unnamedCompany, type Company } from "./companies";
import { REGIMES, type Regime } from "./regimes";

/**
 * LE PAIEMENT — une facture, puis Stripe.
 *
 *   POST /v2/domains/:d/events/:e/invoicesV2        api-v1   crée la session de paiement
 *   GET  /v2/domains/:d/events/:e/invoicesV2/last   api-v1   les coordonnées déjà données
 *
 * ⚠️ IL VIT À LA CONFIRMATION, ET CE N'EST PAS UN CHOIX DE MISE EN PAGE. La route de
 * création demande le montant au serveur SANS lui passer de sélection : il le calcule
 * d'après ce qui est ENREGISTRÉ en base. Or la commande n'est écrite qu'à la
 * confirmation (voir `funnel/participant.ts`). Un bouton « Payer » à l'étape
 * billetterie facturerait donc la commande PRÉCÉDENTE — le montant affiché juste
 * au-dessus et celui débité seraient deux nombres différents. La V1 place son bloc de
 * paiement au même endroit, pour la même raison.
 *
 * ⚠️ ON QUITTE LE SITE. Stripe Checkout est une page hébergée chez eux : le navigateur
 * s'en va, et revient par une adresse que le serveur construit. Tout ce qui n'est pas
 * enregistré à ce moment-là est perdu — d'où l'ordre imposé ci-dessus, encore.
 *
 * ⚠️ ET C'EST LE SERVEUR QUI CONSTATE LE PAIEMENT, PAS NOUS. Stripe renvoie sur
 * `…/invoicesV2/confirm`, qui marque la facture payée, la numérote, écrit les lignes,
 * envoie l'e-mail de confirmation et met `UserEvent.isPaid`. Le front n'apprend que le
 * résultat, par un `?status=success` posé sur l'adresse de retour. Croire un paramètre
 * d'URL sur parole serait une porte ouverte ; on ne s'en sert donc que pour DIRE ce qui
 * s'est passé, jamais pour décider qu'une facture est réglée — cette question-là se
 * repose au serveur.
 */

/* Les six régimes vivent dans `regimes.ts`, sans dépendance au client HTTP : le
   catalogue du panneau les lit pour offrir le choix de ceux qu'on propose. */
export { REGIMES } from "./regimes";

/*
 * ⚠️ TOUT SE FACTURE TTC (#544), QUEL QUE SOIT LE RÉGIME. Les sociétés hors de France
 * (types 2 et 3) étaient facturées hors taxes, en autoliquidation ; elles paient
 * désormais la TVA comme les autres. Le régime ne décide plus que du libellé de la
 * facture et des champs demandés, plus du montant : c'est le serveur
 * (`api-v1/routesV2/invoicesV2/createInvoice`) qui le chiffre, toujours avec TVA.
 */

/** Les coordonnées de facturation, sous les noms qu'attend la route. */
export type Bill = {
  type?: number;
  social_reason?: string;
  address?: string;
  address_more?: string;
  postal_code?: string;
  city?: string;
  /* Un code ISO (« BE »), choisi dans une liste : voir `billCountries.ts`. */
  country?: string;
  intra_communautaire?: string;
};

/**
 * LE FORMULAIRE DEMANDE-T-IL CE CHAMP, POUR CE RÉGIME ?
 *
 * ⚠️ LE PAYS SE DEMANDE À TOUS, SAUF À UNE SOCIÉTÉ EN FRANCE (#549). Il n'était
 * proposé qu'aux sociétés 2 et 3 : un particulier qui habite à l'étranger (bug #502)
 * n'avait nulle part où l'écrire, et sa facture partait sans pays. Seule la société en
 * France n'a rien à préciser ; tant que le régime n'est pas choisi, il reste proposé.
 *
 * ⚠️ LE N° DE TVA INTRACOMMUNAUTAIRE, LUI, RESTE AUX SOCIÉTÉS 2 ET 3 : les autres
 * régimes n'en ont pas.
 */
export function asks(key: keyof Bill, type: number | undefined): boolean {
  if (key === "country") return type !== 1;
  if (key === "intra_communautaire") return type === 2 || type === 3;
  return true;
}

/**
 * CE QUE LE FORMULAIRE EXIGE, POUR CE RÉGIME — l'écran en tire ses astérisques.
 *
 * ⚠️ LE PAYS N'EST EXIGÉ QUE DES SOCIÉTÉS 2 ET 3. Proposé à tous (#549), il reste
 * facultatif pour le particulier, la collectivité et l'établissement public : les
 * faire buter sur un champ que la plupart d'entre eux n'ont pas à remplir les
 * empêcherait de payer pour rien.
 *
 * ⚠️ LE N° DE TVA INTRACOMMUNAUTAIRE N'EST JAMAIS EXIGÉ (#544) : une société hors
 * Europe n'en a pas, et l'exiger l'empêchait de payer. Tout étant facturé TTC, il ne
 * décide plus de rien ; il s'imprime sur la facture quand il est donné.
 */
export function requiredFields(
  type: number | undefined,
): ReadonlyArray<keyof Bill> {
  return [
    "social_reason",
    "address",
    "postal_code",
    "city",
    ...(type === 2 || type === 3 ? (["country"] as const) : []),
  ];
}

/**
 * LA FACTURE TELLE QU'ELLE PART : sans les champs que le régime ne demande pas.
 *
 * ⚠️ UN CHAMP MASQUÉ GARDE SA VALEUR. « Belgique » choisie sous « Société en Europe »,
 * puis le régime changé pour « Société en France » : le pays disparaît de l'écran mais
 * reste dans l'état, et s'imprimait sur la facture d'une société française. Le serveur
 * réécrit la colonne entière à chaque facture : ne pas l'envoyer suffit à l'effacer.
 */
export function sentBill(bill: Bill): Bill {
  return Object.fromEntries(
    Object.entries(bill).filter(([key]) => asks(key as keyof Bill, bill.type)),
  ) as Bill;
}

/**
 * CE QUI MANQUE POUR POUVOIR FACTURER : le régime, puis les champs exigés (voir
 * `requiredFields`).
 *
 * Rendue en liste de CLÉS plutôt qu'en booléen : l'écran s'en sert pour marquer les
 * champs fautifs, et une fonction pure se teste.
 */
export function missing(
  bill: Bill,
  /* Les régimes que l'événement propose : un régime retiré par l'organisateur manque
     autant qu'un régime jamais choisi. */
  offered: ReadonlyArray<Regime> = REGIMES,
): ReadonlyArray<keyof Bill> {
  const required = requiredFields(bill.type);
  return [
    /**
     * ⚠️ LE RÉGIME SE JUGE CONTRE LA LISTE, PAS SUR SA SEULE PRÉSENCE. Compté comme
     * les autres champs — « un nombre fini suffit » — il laissait passer `0`, que le
     * formulaire produit tout seul : son option vide vaut `""`, et `Number("")` vaut
     * zéro. Revenir sur « Choisissez… » après avoir choisi rendait donc le formulaire
     * complet aux yeux de cette fonction, avec un régime qui n'existe pas.
     *
     * Ce n'est pas cosmétique : la facture partait au serveur sous un type que ses six
     * branches ne reconnaissent pas.
     */
    ...(offered.some((r) => r.type === bill.type) ? [] : (["type"] as const)),
    ...required.filter((key) => (bill[key] ?? "").toString().trim() === ""),
    /* Rempli mais pas en cinq chiffres pour un régime français (bug #505). */
    ...(badFrenchPostalCode(bill) ? (["postal_code"] as const) : []),
  ];
}

/**
 * UN CODE POSTAL FRANÇAIS COMPTE CINQ CHIFFRES (bug #505).
 *
 * « 4200 » pour « 42000 » partait vers Stripe sans remarque et s'imprimait sur la
 * facture. Seuls les régimes français PAR DÉFINITION le contrôlent : société en France,
 * collectivité territoriale, établissement public. Le particulier (4) peut habiter à
 * l'étranger (bug #502), les sociétés 2 et 3 y sont : leur code garde sa forme.
 *
 * Vide, il ne compte pas ici : il manque, et `missing` le dit déjà.
 */
const FRENCH_REGIMES: ReadonlyArray<number> = [1, 5, 6];

export function badFrenchPostalCode(bill: Bill): boolean {
  if (bill.type === undefined || !FRENCH_REGIMES.includes(bill.type)) {
    return false;
  }
  const code = (bill.postal_code ?? "").trim();
  return code !== "" && !/^\d{5}$/.test(code);
}

/**
 * OUVRE LE PAIEMENT — rend l'adresse de la page Stripe.
 *
 * ⚠️ `referer` EST L'ADRESSE DE RETOUR, ET ELLE PART NUE. Le serveur la recolle dans
 * l'URL qu'il donne à Stripe (`&referer=${referer}`) puis lui ajoute `?status=success`
 * au retour : une adresse qui porterait déjà une chaîne de requête, ou une esperluette,
 * ressortirait tronquée. On envoie donc origine + chemin, sans plus — exactement ce que
 * fait la V1.
 *
 * ⚠️ LA ROUTE REFUSE SI L'ÉVÉNEMENT N'A PAS DE CLÉ STRIPE (400, « Settings FO PAY is
 * required ») ou si le montant est sous 0,50 € (400, « Amount too small »). Les deux
 * sont des refus légitimes, pas des pannes : l'écran doit les dire autrement qu'en
 * « une erreur est survenue ».
 */
export async function createPayment(
  domainId: number,
  eventId: number,
  bill: Bill,
  referer: string,
): Promise<string> {
  const answer = await apiFetch<{ session?: { url?: string } }>(
    `/v2/domains/${domainId}/events/${eventId}/invoicesV2`,
    { method: "POST", body: { referer, bill } },
  );

  const url = answer?.session?.url;
  if (typeof url !== "string" || url === "") {
    // Sans adresse, il n'y a nulle part où aller : le dire vaut mieux qu'une
    // redirection vers `undefined`, qui sortirait le visiteur du site sans un mot.
    throw new Error("La page de paiement n'a pas pu être ouverte.");
  }
  return url;
}

/**
 * LES COORDONNÉES DÉJÀ DONNÉES, pour ne pas les redemander.
 *
 * ⚠️ ELLE REND LA DERNIÈRE FACTURE, PAYÉE OU NON. C'est ce qu'on veut ici : quelqu'un
 * qui a abandonné devant Stripe retrouve son formulaire rempli, et celui qui revient
 * acheter une option n'a pas à ressaisir son adresse.
 */
export async function fetchLastInvoice(
  domainId: number,
  eventId: number,
  signal?: AbortSignal,
): Promise<Bill> {
  const answer = await apiFetch<{ invoice?: { bill?: unknown } | null } | null>(
    `/v2/domains/${domainId}/events/${eventId}/invoicesV2/last`,
    { signal },
  );
  return readBill(answer?.invoice?.bill);
}

/**
 * CE QUE LA COLONNE PORTE, RAMENÉ AUX CHAMPS DU FORMULAIRE.
 *
 * ⚠️ LA FACTURE EN GARDE DEUX JEUX DE NOMS. Au moment de créer la facture, le serveur
 * recopie chaque champ sous un SECOND nom (`address` → `address1`, `postal_code` →
 * `postalCode`, `social_reason` → `companyName`…) et garde les deux dans la même
 * colonne. Ce sont les noms d'ORIGINE qu'il faut relire : ce sont ceux qu'il attend
 * quand on la lui renvoie.
 *
 * ⚠️ LE PAYS REVIENT EN CODE, ou ne revient pas : une facture payée avant le sélecteur
 * porte un nom libre (voir `countryCode`).
 */
export function readBill(raw: unknown): Bill {
  if (!raw || typeof raw !== "object") return {};
  const source = raw as Record<string, unknown>;

  const text = (key: string): string | undefined =>
    typeof source[key] === "string" && source[key] !== ""
      ? (source[key] as string)
      : undefined;

  const type = Number(source.type);
  const country = countryCode(text("country"));

  return {
    ...(Number.isFinite(type) && type > 0 ? { type } : {}),
    ...(text("social_reason") ? { social_reason: text("social_reason") } : {}),
    ...(text("address") ? { address: text("address") } : {}),
    ...(text("address_more") ? { address_more: text("address_more") } : {}),
    ...(text("postal_code") ? { postal_code: text("postal_code") } : {}),
    ...(text("city") ? { city: text("city") } : {}),
    ...(country ? { country } : {}),
    ...(text("intra_communautaire")
      ? { intra_communautaire: text("intra_communautaire") }
      : {}),
  };
}

/** Une adresse sous les noms des colonnes : `Users` et `Companies` les partagent. */
export type PostalAddress = {
  address1?: string;
  address2?: string;
  postalCode?: string;
  city?: string;
};

/** La facture porte-t-elle déjà une adresse, même partielle ? */
export function hasAddress(bill: Bill): boolean {
  return [bill.address, bill.postal_code, bill.city].some(
    (v) => (v ?? "").trim() !== "",
  );
}

const hasPostal = (a: PostalAddress | undefined): a is PostalAddress =>
  !!a &&
  [a.address1, a.postalCode, a.city].some((v) => (v ?? "").trim() !== "");

/**
 * LES COORDONNÉES DÉJÀ CONNUES, POUR NE PAS TOUT RETAPER (bug #358).
 *
 * ⚠️ LA DERNIÈRE FACTURE PRIME, ENTIÈRE. C'est ce que la personne a écrit pour être
 * facturée ; un profil ne la corrige pas.
 *
 * ⚠️ SANS ELLE, LA SOCIÉTÉ REPRÉSENTÉE À CET ÉVÉNEMENT, PUIS LA PERSONNE. Celle qui vient
 * pour une société est facturée à son nom : on reprend son nom ET son adresse. Sinon,
 * l'adresse de la personne, sans son nom : « Nom ou raison sociale » pré-rempli d'un
 * nom propre ferait partir à son nom la facture d'une société qu'elle a oublié de
 * déclarer.
 *
 * ⚠️ UNE ADRESSE NE SE COMPOSE PAS DE DEUX SOURCES. La rue de la société et la ville
 * de la personne feraient une adresse qui n'existe pas : on prend le bloc entier de la
 * première source qui en a un.
 */
export function knownBill(
  last: Bill,
  person: PostalAddress | undefined,
  company: Company | undefined,
): Bill {
  if (hasAddress(last)) return last;
  const source = hasPostal(company) ? company : hasPostal(person) ? person : null;
  if (!source) return last;
  const text = (v: string | undefined) => (v ?? "").trim();
  return {
    /* Une fiche sans nom se nomme « Société 12 » dans les listes : pas sur une facture. */
    ...(source === company && company.name !== unnamedCompany(company.id)
      ? { social_reason: company.name }
      : {}),
    ...(text(source.address1) ? { address: text(source.address1) } : {}),
    ...(text(source.address2) ? { address_more: text(source.address2) } : {}),
    ...(text(source.postalCode) ? { postal_code: text(source.postalCode) } : {}),
    ...(text(source.city) ? { city: text(source.city) } : {}),
    ...last,
  };
}

/**
 * REVIENT-ON DE STRIPE ?
 *
 * ⚠️ SEUL LE SUCCÈS EST ANNONCÉ, et l'abandon ne l'est pas — parce que le serveur ne
 * l'annonce pas : sur `cancel` il renvoie sur l'adresse NUE, sans paramètre. Un
 * abandon est donc indiscernable d'une arrivée ordinaire, et c'est très bien ainsi :
 * quelqu'un qui a renoncé retrouve son écran avec son bouton, sans qu'on lui reproche
 * d'avoir fermé la page.
 *
 * ⚠️ ET CE PARAMÈTRE NE PROUVE RIEN. N'importe qui peut l'écrire dans la barre
 * d'adresse. Ce que l'on doit encore est relu auprès du serveur, qui seul a vu Stripe.
 *
 * ⚠️ IL NE DIT PAS NON PLUS À QUI EST LE PAIEMENT (bug 8). Le retour de Stripe est un
 * chargement neuf, sous le jeton que le navigateur tient À CE MOMENT-LÀ : si un autre
 * onglet a ouvert le compte de B pendant que A payait, A revient en B. Le serveur nomme
 * donc la facture payée (`?invoice=<id>&status=success`, api-v1 `confirmInvoice`), et
 * rien ne se dit ni ne s'envoie avant que `fetchReturnedInvoice` l'ait retrouvée
 * comme la facture de la session courante (voir `Checkout`).
 *
 * Rend l'id de la facture revenue, ou `null` : pas de retour réussi, ou un retour qui
 * ne nomme pas sa facture (lien ancien, adresse tapée), et qui ne prouve donc rien.
 */
export function paymentReturn(search: string): number | null {
  const params = new URLSearchParams(search);
  if (params.get("status") !== "success") return null;
  const id = Number(params.get("invoice"));
  return Number.isInteger(id) && id > 0 ? id : null;
}

/** Ce qu'il faut de la dernière facture pour reconnaître un retour de paiement. */
export type ReturnedInvoice = { id: number; userId: number; isPaid: boolean };

/**
 * LA DERNIÈRE FACTURE DE LA SESSION COURANTE, telle que le serveur la tient.
 *
 * `invoicesV2/last` ne rend que la facture du titulaire du jeton (jamais celle d'un
 * autre, hors organisateur qui la demande nommément) : c'est le serveur qui compare
 * l'identité du paiement à celle de la session. Un retour dont la facture n'est pas
 * celle-ci n'appartient pas à la personne connectée.
 */
export async function fetchReturnedInvoice(
  domainId: number,
  eventId: number,
  signal?: AbortSignal,
): Promise<ReturnedInvoice | null> {
  const answer = await apiFetch<{ invoice?: Record<string, unknown> | null } | null>(
    `/v2/domains/${domainId}/events/${eventId}/invoicesV2/last`,
    { signal },
  );
  const invoice = answer?.invoice;
  if (!invoice || typeof invoice.id !== "number" || typeof invoice.userId !== "number") {
    return null;
  }
  return {
    id: invoice.id,
    userId: invoice.userId,
    // MySQL rend un booléen ou 0/1 selon le pilote.
    isPaid: invoice.isPaid === true || invoice.isPaid === 1,
  };
}
